import api from '../../api';

/* ==========================================================================
   INVENTORY ITEMS
   ========================================================================== */

/**
 * Get inventory items list.
 *
 * @param {Object} [params]
 * @param {string} [params.search]
 * @param {string} [params.category]
 * @param {boolean} [params.is_active]
 * @param {number} [params.page]
 *
 * @returns {Promise<Object>} Paginated inventory items response
 */
export const getItems = async (params = {}) => {
  try {
    const response = await api.get("/inventory/inventory-items/", {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get single inventory item.
 *
 * @param {number|string} id - Inventory item ID
 *
 * @returns {Promise<Object>} Inventory item object
 */
export const getItem = async (id) => {
  try {
    const response = await api.get(
      `/inventory/inventory-items/${id}/`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create inventory item.
 *
 * @param {Object} data - Inventory item payload
 *
 * @returns {Promise<Object>} Created inventory item
 */
export const createItem = async (data) => {
  try {
    const response = await api.post(
      "/inventory/inventory-items/",
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update inventory item.
 *
 * @param {number|string} id - Inventory item ID
 * @param {Object} data - Updated inventory item payload
 *
 * @returns {Promise<Object>} Updated inventory item
 */
export const updateItem = async (id, data) => {
  try {
    const response = await api.put(
      `/inventory/inventory-items/${id}/`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Soft delete inventory item.
 *
 * @param {number|string} id - Inventory item ID
 *
 * @returns {Promise<Object>} Delete response
 */
export const deleteItem = async (id) => {
  try {
    const response = await api.delete(
      `/inventory/inventory-items/${id}/`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/* ==========================================================================
   MONTHLY INVENTORY BAGS
   ========================================================================== */

/**
 * Get inventory bags list.
 *
 * @param {Object} [params]
 * @param {string} [params.status]
 * @param {number} [params.technician_id]
 * @param {number} [params.month]
 * @param {number} [params.year]
 * @param {number} [params.page]
 *
 * @returns {Promise<Object>} Paginated bags response
 */
export const getBags = async (params = {}) => {
  try {
    const response = await api.get(
      "/inventory/inventory-bags/",
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get single inventory bag.
 *
 * @param {number|string} id - Bag ID
 *
 * @returns {Promise<Object>} Inventory bag object
 */
export const getBag = async (id) => {
  try {
    const response = await api.get(
      `/inventory/inventory-bags/${id}/`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create inventory bag.
 *
 * @param {Object} data - Bag payload
 *
 * @returns {Promise<Object>} Created bag
 */
export const createBag = async (data) => {
  try {
    const response = await api.post(
      "/inventory/inventory-bags/",
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update inventory bag.
 *
 * @param {number|string} id - Bag ID
 * @param {Object} data - Partial bag update payload
 *
 * @returns {Promise<Object>} Updated bag
 */
export const updateBag = async (id, data) => {
  try {
    const response = await api.patch(
      `/inventory/inventory-bags/${id}/`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Close inventory bag.
 *
 * @param {number|string} id - Bag ID
 *
 * @returns {Promise<Object>} Closed bag response
 */
export const closeBag = async (id) => {
  try {
    const response = await api.post(
      `/inventory/inventory-bags/${id}/close/`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Assign inventory items to bag.
 *
 * Expected payload format:
 * {
 *   items: [
 *     {
 *       inventory_item,
 *       quantity_assigned,
 *       quantity_remaining,
 *       low_stock_threshold
 *     }
 *   ]
 * }
 *
 * @param {number|string} bagId - Bag ID
 * @param {Array<Object>} items - Inventory items array
 *
 * @returns {Promise<Object>} Assignment response
 */
export const assignInventory = async (bagId, items) => {
  try {
    const response = await api.post(
      `/inventory/inventory-bags/${bagId}/assign-inventory/`,
      { items }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Add consumable inventory items to bag.
 *
 * @param {number|string} bagId - Bag ID
 * @param {Array<Object>} items - Consumable items array
 *
 * @returns {Promise<Object>} API response
 */
export const addConsumables = async (bagId, items) => {
  try {
    const response = await api.post(
      `/inventory/inventory-bags/${bagId}/add-consumables/`,
      { items }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Add capital inventory items to bag.
 *
 * @param {number|string} bagId - Bag ID
 * @param {Array<Object>} items - Capital items array
 *
 * @returns {Promise<Object>} API response
 */
export const addCapitalItems = async (bagId, items) => {
  try {
    const response = await api.post(
      `/inventory/inventory-bags/${bagId}/add-capital-items/`,
      { items }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Dispatch inventory bag to another technician.
 *
 * Expected payload:
 * {
 *   to_technician,
 *   transferred_by,
 *   reason,
 *   transferred_at
 * }
 *
 * @param {number|string} bagId - Bag ID
 * @param {Object} data - Dispatch payload
 *
 * @returns {Promise<Object>} Updated bag response
 */
export const dispatchBag = async (bagId, data) => {
  try {
    const response = await api.post(
      `/inventory/inventory-bags/${bagId}/dispatch/`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get technician inventory.
 *
 * @param {number|string} technicianId - Technician ID
 *
 * @returns {Promise<Object>} Technician inventory response
 */
export const getTechnicianInventory = async (technicianId) => {
  try {
    const response = await api.get(
      "/inventory/inventory-bags/technician-inventory/",
      {
        params: {
          technician_id: technicianId,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};



/* ==========================================================================
   SERVICE ATTRIBUTE CONSUMABLE MAPPING  (NEW — attribute-level deduction)
   ========================================================================== */

/**
 * Get all attribute mappings.
 * Optional filter: ?service=<id>&is_active=true
 *
 * @param {Object} [params]
 * @param {number} [params.service]
 * @param {boolean} [params.is_active]
 * @returns {Promise<Object>} Paginated attribute mappings
 */
export const getAttributeMappings = async (params = {}) => {
  try {
    const response = await api.get("/inventory/attribute-mappings/", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get attribute mappings for one service, grouped by attribute name.
 * Returns: { service_id, attributes: { hbsag: [...], rbs: [...] } }
 *
 * @param {number|string} serviceId
 * @returns {Promise<Object>}
 */
export const getAttributeMappingsByService = async (serviceId) => {
  try {
    const response = await api.get("/inventory/attribute-mappings/by-service/", {
      params: { service: serviceId },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create a single attribute mapping.
 *
 * @param {Object} data
 * @param {number} data.service
 * @param {string} data.attribute_name   e.g. "hbsag"
 * @param {number} data.inventory_item
 * @param {number} data.quantity_per_use
 * @returns {Promise<Object>} Created mapping
 */
export const createAttributeMapping = async (data) => {
  try {
    const response = await api.post("/inventory/attribute-mappings/", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Bulk-create attribute mappings for one service in a single call.
 *
 * @param {number} serviceId
 * @param {Array<{attribute_name, inventory_item, quantity_per_use}>} mappings
 * @returns {Promise<Object>} { created: [...] }
 */
export const bulkCreateAttributeMappings = async (serviceId, mappings) => {
  try {
    const response = await api.post("/inventory/attribute-mappings/bulk-create/", {
      service: serviceId,
      mappings,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update an attribute mapping (partial).
 *
 * @param {number|string} id
 * @param {Object} data
 * @returns {Promise<Object>} Updated mapping
 */
export const updateAttributeMapping = async (id, data) => {
  try {
    const response = await api.patch(`/inventory/attribute-mappings/${id}/`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete an attribute mapping.
 *
 * @param {number|string} id
 * @returns {Promise<Object>}
 */
export const deleteAttributeMapping = async (id) => {
  try {
    const response = await api.delete(`/inventory/attribute-mappings/${id}/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};



/* ==========================================================================
   SERVICE CONSUMABLE MAPPING
   ========================================================================== */

/**
 * Get service consumable mappings.
 *
 * @param {Object} [params]
 * @param {number} [params.service]
 * @param {number} [params.inventory_item]
 * @param {number} [params.page]
 *
 * @returns {Promise<Object>} Paginated mappings response
 */
export const getMappings = async (params = {}) => {
  try {
    const response = await api.get(
      "/inventory/service-mappings/",
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get single service mapping.
 *
 * @param {number|string} id - Mapping ID
 *
 * @returns {Promise<Object>} Mapping object
 */
export const getMapping = async (id) => {
  try {
    const response = await api.get(
      `/inventory/service-mappings/${id}/`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create service consumable mapping.
 *
 * @param {Object} data - Mapping payload
 *
 * @returns {Promise<Object>} Created mapping
 */
export const createMapping = async (data) => {
  try {
    const response = await api.post(
      "/inventory/service-mappings/",
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update service consumable mapping.
 *
 * @param {number|string} id - Mapping ID
 * @param {Object} data - Updated mapping payload
 *
 * @returns {Promise<Object>} Updated mapping
 */
export const updateMapping = async (id, data) => {
  try {
    const response = await api.put(
      `/inventory/service-mappings/${id}/`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete service consumable mapping.
 *
 * @param {number|string} id - Mapping ID
 *
 * @returns {Promise<Object>} Delete response
 */
export const deleteMapping = async (id) => {
  try {
    const response = await api.delete(
      `/inventory/service-mappings/${id}/`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get mappings by service ID.
 *
 * @param {number|string} serviceId - Service ID
 *
 * @returns {Promise<Object>} Mappings response
 */
export const getMappingsByService = async (serviceId) => {
  try {
    const response = await api.get(
      "/inventory/service-mappings/by-service/",
      {
        params: {
          service: serviceId,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/* ==========================================================================
   CONSUMPTION LOGS
   ========================================================================== */

/**
 * Get inventory usage logs.
 *
 * @param {Object} [params]
 * @param {number} [params.camp]
 * @param {number} [params.inventory_item]
 * @param {string} [params.usage_type]
 * @param {number} [params.page]
 *
 * @returns {Promise<Object>} Paginated usage logs response
 */
export const getUsageLogs = async (params = {}) => {
  try {
    const response = await api.get(
      "/inventory/inventory-usage/",
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create manual inventory usage log.
 *
 * @param {Object} data - Usage payload
 *
 * @returns {Promise<Object>} Created usage log
 */
export const createManualUsage = async (data) => {
  try {
    const response = await api.post(
      "/inventory/inventory-usage/",
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/* ==========================================================================
   LOW STOCK ALERTS
   ========================================================================== */

/**
 * Get low stock alerts.
 *
 * @param {Object} [params]
 * @param {boolean} [params.resolved]
 * @param {number} [params.technician]
 * @param {number} [params.page]
 *
 * @returns {Promise<Object>} Paginated alerts response
 */
export const getAlerts = async (params = {}) => {
  try {
    const response = await api.get(
      "/inventory/low-stock-alerts/",
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get unresolved low stock alerts.
 *
 * @returns {Promise<Object>} Unresolved alerts response
 */
export const getUnresolvedAlerts = async () => {
  try {
    const response = await api.get(
      "/inventory/low-stock-alerts/unresolved/"
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Resolve low stock alert.
 *
 * @param {number|string} id - Alert ID
 *
 * @returns {Promise<Object>} Resolved alert response
 */
export const resolveAlert = async (id) => {
  try {
    const response = await api.post(
      `/inventory/low-stock-alerts/${id}/resolve/`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/* ==========================================================================
   REFILL LOGS
   ========================================================================== */

/**
 * Get refill logs.
 *
 * @param {Object} [params]
 * @param {number} [params.inventory_bag]
 * @param {number} [params.inventory_item]
 * @param {number} [params.page]
 *
 * @returns {Promise<Object>} Paginated refill logs response
 */
export const getRefillLogs = async (params = {}) => {
  try {
    const response = await api.get(
      "/inventory/refill-logs/",
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create refill log.
 *
 * @param {Object} data - Refill payload
 *
 * @returns {Promise<Object>} Created refill log
 */
export const createRefill = async (data) => {
  try {
    const response = await api.post(
      "/inventory/refill-logs/",
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get refill logs by bag.
 *
 * @param {number|string} bagId - Bag ID
 *
 * @returns {Promise<Object>} Refill logs response
 */
export const getRefillsByBag = async (bagId) => {
  try {
    const response = await api.get(
      "/inventory/refill-logs/by-bag/",
      {
        params: {
          bag: bagId,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/* ==========================================================================
   AUDIT LOGS
   ========================================================================== */

/**
 * Get audit logs.
 *
 * @param {Object} [params]
 * @param {string} [params.action]
 * @param {number} [params.performed_by]
 * @param {number} [params.page]
 *
 * @returns {Promise<Object>} Paginated audit logs response
 */
export const getAuditLogs = async (params = {}) => {
  try {
    const response = await api.get(
      "/inventory/audit-logs/",
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/* ==========================================================================
   TRANSFER LOGS
   ========================================================================== */

/**
 * Get transfer logs.
 *
 * @param {Object} [params]
 * @param {number} [params.inventory_bag]
 * @param {number} [params.from_technician]
 * @param {number} [params.to_technician]
 * @param {number} [params.page]
 *
 * @returns {Promise<Object>} Paginated transfer logs response
 */
export const getTransferLogs = async (params = {}) => {
  try {
    const response = await api.get(
      "/inventory/transfer-logs/",
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};


/**
 * Get available technicians for transfer dropdown.
 *
 * @param {number|string} [excludeBagId] - Bag ID to exclude its current technician
 * @returns {Promise<Array>} List of technicians
 */
export const getAvailableTechnicians = async (excludeBagId = null) => {
  try {
    const params = {};
    if (excludeBagId) params.exclude_bag = excludeBagId;
    const response = await api.get(
      "/inventory/inventory-bags/available-technicians/",
      { params }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get transfer logs for a specific bag.
 *
 * @param {number|string} bagId - Bag ID
 * @returns {Promise<Object>} Transfer logs response
 */
export const getTransfersByBag = async (bagId) => {
  try {
    const response = await api.get("/inventory/transfer-logs/by-bag/", {
      params: { bag: bagId },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};


/* ==========================================================================
   ANALYTICS
   ========================================================================== */

/**
 * Get camp summary analytics.
 *
 * @param {Object} [params]
 * @param {number} [params.month]
 * @param {number} [params.year]
 * @param {number} [params.camp_id]
 *
 * @returns {Promise<Array>} Camp summary analytics
 */
export const getCampSummary = async (params = {}) => {
  try {
    const response = await api.get(
      "/inventory/analytics/camp-summary/",
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get technician summary analytics.
 *
 * @param {Object} [params]
 * @param {number} [params.month]
 * @param {number} [params.year]
 *
 * @returns {Promise<Array>} Technician summary analytics
 */
export const getTechnicianSummary = async (params = {}) => {
  try {
    const response = await api.get(
      "/inventory/analytics/technician-summary/",
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get top inventory items analytics.
 *
 * @param {Object} [params]
 * @param {number} [params.limit]
 * @param {number} [params.month]
 * @param {number} [params.year]
 *
 * @returns {Promise<Array>} Top items analytics
 */
export const getTopItems = async (params = {}) => {
  try {
    const response = await api.get(
      "/inventory/analytics/top-items/",
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get monthly inventory usage trend analytics.
 *
 * @param {Object} [params]
 * @param {number} [params.months_back]
 *
 * @returns {Promise<Array>} Monthly trend analytics
 */
export const getMonthlyTrend = async (params = {}) => {
  try {
    const response = await api.get(
      "/inventory/analytics/monthly-trend/",
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchCampTechnicianUsage = async (campId, technicianId) => {
  try {
    const response = await api.get(
      `/inventory/analytics/camp-technician-usage/`,
      { params: { camp_id: campId, technician_id: technicianId } }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const submitExtraUsage = async (payload) => {
  try {
    const response = await api.post(
      `/inventory/inventory-usage/extra-usage/`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchInventorySummary = async (technicianId) => {
  try {
    const response = await api.get(`/inventory/summary/${technicianId}/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};


export const addStock = async (id, quantity, remarks = "") => {
  try {
    const response = await api.post(
      `/inventory/inventory-items/${id}/add-stock/`,
      { quantity, remarks }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};



export const getCentralRefillLogs = async (itemId) => {
  try {
    const response = await api.get(`/inventory/inventory-items/${itemId}/refill-history/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// export const fetchCapitalUsageLogs = async (itemId) => {
//   try {
// const response = await api.get(`/inventory/inventory-usage/`, {
//   params: { inventory_item_id: itemId }
// });
//     return response.data;
//   } catch (error) {
//     throw error;
//   }
// };

/* ==========================================================================
   INVENTORY USAGE CONFIRMATIONS
   ========================================================================== */

/**
 * Confirm a single usage log.
 * POST /api/inventory/confirmations/
 *
 * @param {{ usage_log: number, remarks?: string }} payload
 */
export const confirmUsageLog = async (payload) => {
  const response = await api.post('/inventory/confirmations/', payload);
  return response.data;
};

/**
 * List all confirmations (coordinator view) with optional filters.
 *
 * @param {Object} params – technician_id, camp_id, status, start_date, end_date, item_name, page
 */
export const getConfirmations = async (params = {}) => {
  const response = await api.get('/inventory/confirmations/', { params });
  return response.data;
};

/**
 * Fetch confirmation dashboard summary.
 * GET /api/inventory/confirmations/summary/
 */
export const getConfirmationSummary = async () => {
  const response = await api.get('/inventory/confirmations/summary/');
  return response.data;
};

/**
 * Fetch pending confirmations for a technician.
 * GET /api/inventory/confirmations/?technician_id=X&status=PENDING
 *
 * @param {number} technicianId
 */
export const getPendingConfirmations = async (technicianId) => {
  const response = await api.get('/inventory/confirmations/', {
    params: { technician_id: technicianId, status: 'PENDING' },
  });
  return response.data;
};


export const submitDailyUsageConfirmation = async (data) => {
  try {
    const response = await api.post(
      "/inventory/daily-usage-confirmation/",
      data
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getTodayConfirmation = async (
  technicianId,
  campId
) => {

  const response = await api.get(
    "/inventory/daily-usage-confirmation/",
    {
      params: {
        technician_id: technicianId,
        camp_id: campId
      }
    }
  );

  return response.data;
};








/* ==========================================================================
   DAILY & MONTHLY VERIFICATION (Analytics Dashboard)
   ========================================================================== */

/**
 * Get daily inventory usage / verification status for technicians.
 * GET /inventory/daily-usage-status/
 *
 * Sample response:
 * [
 *   { technician_id: 1, technician_name: "aman", status: "Pending", confirmed_at: null },
 *   { technician_id: 5, technician_name: "abey", status: "Submitted", confirmed_at: "2026-06-25T09:13:29.467432Z" }
 * ]
 *
 * @param {Object} [params]
 * @param {string} [params.date] - YYYY-MM-DD, defaults to today on the backend
 * @param {number} [params.camp_id]
 *
 * @returns {Promise<Array>} Daily verification status per technician
 */
export const getDailyUsageStatus = async (params = {}) => {
  try {
    const response = await api.get("/inventory/daily-usage-status/", {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get monthly inventory verification & reconciliation for a single bag.
 * GET /inventory/monthly-verification/?bag_id=<id>
 *
 * Sample response:
 * {
 *   bag_id: 1,
 *   technician: "Rahul",
 *   items: [
 *     { inventory_item_id, item_name, assigned_quantity, used_quantity,
 *       remaining_quantity, expected_remaining, difference, status }
 *   ]
 * }
 *
 * @param {number|string} bagId - Bag ID
 *
 * @returns {Promise<Object>} Monthly verification & reconciliation response
 */
export const getMonthlyVerification = async (bagId) => {
  try {
    const response = await api.get("/inventory/monthly-verification/", {
      params: { bag_id: bagId },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};





/* ==========================================================================
   PURCHASE BATCHES (FIFO procurement tracking)
   ========================================================================== */

/**
 * Get purchase batches list.
 *
 * @param {Object} [params]
 * @param {number|string} [params.inventory_item]
 * @param {string} [params.supplier]
 * @param {string} [params.date_from] - YYYY-MM-DD
 * @param {string} [params.date_to] - YYYY-MM-DD
 * @param {number} [params.page]
 *
 * @returns {Promise<Object>} Paginated purchase batches response
 */
export const getPurchaseBatches = async (params = {}) => {
  try {
    const response = await api.get("/inventory/purchase-batches/", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get single purchase batch with consumption history (FIFO deductions).
 *
 * @param {number|string} id - Purchase batch ID
 *
 * @returns {Promise<Object>} Purchase batch detail object
 */
export const getPurchaseBatch = async (id) => {
  try {
    const response = await api.get(`/inventory/purchase-batches/${id}/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create purchase batch.
 *
 * Expected payload format:
 * {
 *   inventory_item,
 *   purchase_date,
 *   purchased_quantity,
 *   unit_price,
 *   supplier,
 *   invoice_number,
 *   remarks
 * }
 *
 * @param {Object} data - Purchase batch payload
 *
 * @returns {Promise<Object>} Created purchase batch
 */
export const createPurchaseBatch = async (data) => {
  try {
    const response = await api.post("/inventory/purchase-batches/", data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/* ==========================================================================
   INVENTORY INVOICES (FIFO costing — monthly / camp / bag)
   ========================================================================== */

/**
 * Get monthly inventory invoice (FIFO cost of all items used in a month).
 * GET /inventory/invoices/monthly/?month=7&year=2026
 *
 * @param {Object} params
 * @param {number} params.month
 * @param {number} params.year
 *
 * @returns {Promise<Object>} Monthly invoice response
 */
export const getMonthlyInvoice = async (params = {}) => {
  try {
    const response = await api.get("/inventory/invoices/monthly/", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get camp inventory invoice (FIFO cost of items used by a camp in a month).
 * GET /inventory/invoices/camp/?camp_id=5&month=7&year=2026
 *
 * @param {Object} params
 * @param {number|string} params.camp_id
 * @param {number} params.month
 * @param {number} params.year
 *
 * @returns {Promise<Object>} Camp invoice response
 */
export const getCampInvoice = async (params = {}) => {
  try {
    const response = await api.get("/inventory/invoices/camp/", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get bag inventory invoice (FIFO cost of items used out of a specific bag).
 * GET /inventory/invoices/bag/?bag_id=12
 *
 * @param {Object} params
 * @param {number|string} params.bag_id
 *
 * @returns {Promise<Object>} Bag invoice response
 */
export const getBagInvoice = async (params = {}) => {
  try {
    const response = await api.get("/inventory/invoices/bag/", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};


 export const getAllCampDetail = async (params = {}) => {
  try {
    const response = await api.get("/inventory/analytics/camp-summary/", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/* ==========================================================================
   EXPORTS
   ========================================================================== */

export const inventoryApi = {
  // Inventory Items
  getItems,
  getItem,
  createItem,
  updateItem,
  deleteItem,

  // Monthly Inventory Bags
  getBags,
  getBag,
  createBag,
  updateBag,
  closeBag,
  assignInventory,
  addConsumables,
  addCapitalItems,
  dispatchBag,
  getTechnicianInventory,

  // Service Consumable Mapping
  getMappings,
  getMapping,
  createMapping,
  updateMapping,
  deleteMapping,
  getMappingsByService,


  // Service Attribute Consumable Mapping (NEW)
  getAttributeMappings,
  getAttributeMappingsByService,
  createAttributeMapping,
  bulkCreateAttributeMappings,
  updateAttributeMapping,
  deleteAttributeMapping,

  // Consumption Logs
  getUsageLogs,
  createManualUsage,

  // Low Stock Alerts
  getAlerts,
  getUnresolvedAlerts,
  resolveAlert,

  // Refill Logs
  getRefillLogs,
  createRefill,
  getRefillsByBag,

  // Audit Logs
  getAuditLogs,

  // Transfer Logs
  getTransferLogs,
  getTransfersByBag,      
  getAvailableTechnicians,

  // Analytics
  getCampSummary,
  getTechnicianSummary,
  getTopItems,
  getMonthlyTrend,

  // Additional functions
  fetchCampTechnicianUsage,
  submitExtraUsage,
  fetchInventorySummary,
  // fetchCapitalUsageLogs,


  addStock,
  getCentralRefillLogs,



  confirmUsageLog,
  getConfirmations,
  getConfirmationSummary,
  getPendingConfirmations,
  submitDailyUsageConfirmation,
  getTodayConfirmation,

  // Analytics Dashboard - Verification
  getDailyUsageStatus,
  getMonthlyVerification,

  // Purchase Batches (FIFO)
  getPurchaseBatches,
  getPurchaseBatch,
  createPurchaseBatch,

  // Inventory Invoices (FIFO costing)
  getMonthlyInvoice,
  getCampInvoice,
  getBagInvoice,

  //get camp detail
  getAllCampDetail,
};

export default inventoryApi;